import React, { useEffect, useRef, useState } from 'react';
import { captureVideoFrame } from '../utils/videoHelper';
import Loader from '../shared/Loader';

// LazyImage: loads image when visible. For Vercel, prefer the serverless
// `/api/generate-thumbnail` endpoint first (works around CORS issues and is
// more reliable than in-browser capture). Falling back order:
// 1) server-side thumbnail (via `/api/generate-thumbnail?url=...`)
// 2) in-browser capture (if `videoUrl` provided and allowed by CORS)
// 3) provided `src` if it's already an image
// 4) `defaultSrc` final fallback
const isImageUrl = (u) => {
  if (!u) return false;
  return /\.(jpe?g|png|webp|gif|svg)(?:\?.*)?$/i.test(u);
};

const LazyImage = ({ src, srcSet, sizes, alt = '', className = '', videoUrl = null, videoDuration = null, defaultSrc = '/default-thumbnail.svg', style = {} }) => {
  const imgRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [attemptedServer, setAttemptedServer] = useState(false);

  useEffect(() => {
    setCurrentSrc('');
    setAttemptedServer(false);
    setLoading(true);
    if (!src && !videoUrl) return;

    const el = imgRef.current;
    if (!el) return;

    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      }, { rootMargin: '200px' });
      observer.observe(el);
    } else {
      setInView(true);
    }

    return () => { try { observer && observer.disconnect(); } catch (e) {} };
  }, [src, videoUrl]);

  // Try server-side thumbnail first when item enters view (safer for Vercel)
  useEffect(() => {
    if (!inView) return;
    const original = videoUrl || src;
    if (!original) {
      setCurrentSrc(defaultSrc);
      setLoading(false);
      return;
    }

    // If the provided src is already an image, use it immediately
    if (isImageUrl(src)) {
      setCurrentSrc(src);
      setLoading(false); // Set loading to false immediately for images
      return;
    }

    let aborted = false;

    const tryServer = async () => {
      setAttemptedServer(true);
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        const resp = await fetch(`/api/generate-thumbnail?url=${encodeURIComponent(original)}`, { signal: controller.signal });
        clearTimeout(timeout);
        if (!resp.ok) {
          // 404 or other -> move to next fallback
          return null;
        }
        const j = await resp.json();
        if (j && j.thumbnailUrl) return j.thumbnailUrl;
      } catch (e) {
        // ignore and fallthrough to in-browser capture / default
      }
      return null;
    };

    const doFallbacks = async () => {
      if (aborted) return;
      const serverThumb = await tryServer();
      if (aborted) return;
      if (serverThumb) {
        setCurrentSrc(serverThumb);
        setLoading(false); // Set loading to false when we have a valid thumbnail
        return;
      }

      // Try in-browser capture if this is a video URL
      const originalForCapture = videoUrl || src;
      if (originalForCapture) {
        try {
          const seekTo = videoDuration ? Math.max(1, Math.floor(videoDuration / 2)) : 1;
          const dataUrl = await captureVideoFrame(originalForCapture, seekTo);
          if (dataUrl) {
            setCurrentSrc(dataUrl);
            setLoading(false); // Set loading to false when we have a captured frame
            return;
          }
        } catch (err) {
          // ignore
        }
      }

      // If src itself is an image, use it; else final default
      if (isImageUrl(src)) setCurrentSrc(src);
      else setCurrentSrc(defaultSrc);
      setLoading(false); // Set loading to false for fallback
    };

    doFallbacks();
    return () => { aborted = true; };
  }, [inView, src, videoUrl, videoDuration, defaultSrc]);

  const handleError = (e) => {
    const el = e.target;
    el.onerror = null;
    // If server wasn't attempted yet, try it now
    if (!attemptedServer) {
      (async () => {
        try {
          const original = videoUrl || src;
          const resp = await fetch(`/api/generate-thumbnail?url=${encodeURIComponent(original)}`);
          if (resp.ok) {
            const j = await resp.json();
            if (j && j.thumbnailUrl) {
              setCurrentSrc(j.thumbnailUrl);
              return;
            }
          }
        } catch (err) {}
        // final fallback
        setCurrentSrc(defaultSrc);
      })();
      return;
    }

    // Already attempted server; fall back to final default
    setCurrentSrc(defaultSrc);
  };

  const handleLoad = () => setLoading(false);

  return (
    <div ref={imgRef} className={`relative ${className}`} style={{ display: 'block', ...style }}>
      {loading && currentSrc && currentSrc !== defaultSrc && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-slate-800">
          <Loader />
        </div>
      )}
      <img
        src={currentSrc || defaultSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        className="block h-full w-full"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        loading="lazy"
        onError={handleError}
        onLoad={handleLoad}
      />
    </div>
  );
};

export default LazyImage;
